import { z } from 'zod';
import { pipe } from 'fp-ts/function';
import { array } from 'fp-ts';
import { CustomErrorParams, CustomErrorType, Validation } from '@mews/framework';
import { WithRateGroupId, WithServiceId } from '@mews-commander/types';
import {
    PaymentAndCancellationPolicyOverrideAddParameters,
    PaymentAndCancellationPolicyUpdateParameters,
} from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import {
    FieldSectionName,
    PolicyExceptionsDatesFieldName,
    PaymentPolicyFieldName,
    SettlementConfigurationsFieldName,
} from '../components/forms/types';
import { WithPolicyOverrideId } from '../types';
import { paymentPolicySchema } from './paymentPolicySchema';
import { findOverlappingIntervalIndices } from './utils';

const rateGroupExceptionTypeSchema = z.object({
    exceptionType: z.literal('rateGroup'),
});

const rateExceptionTypeSchema = z.object({
    exceptionType: z.literal('rate'),
    [PaymentPolicyFieldName.Rates]: z.array(z.string()).min(1),
});

const requirePaymentCardDuringOciExceptionTypeSchema = z.object({
    [SettlementConfigurationsFieldName.RequirePaymentCardDuringOnlineCheckIn]: z.boolean().nullish(),
});

export const datesPolicyExceptionSchema = z.object({
    [FieldSectionName.Dates]: z.object({
        [PolicyExceptionsDatesFieldName.TimeUnitIntervals]: z
            .array(
                z.object({
                    firstTimeUnitStartUtc: Validation.DateTime.Utc,
                    lastTimeUnitStartUtc: Validation.DateTime.Utc,
                })
            )
            .superRefine((timeIntervals, ctx) => {
                const overlapIndices = findOverlappingIntervalIndices({ timeIntervals });

                overlapIndices.forEach((overlapIndex) => {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        params: {
                            errorType: CustomErrorType.Other,
                            textKey: TextKey.PolicyExceptionOverlapErrorMessage,
                        } satisfies CustomErrorParams,
                        path: [overlapIndex, PolicyExceptionsDatesFieldName.FirstTimeUnitStartUtc],
                        fatal: true,
                    });

                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        params: {
                            errorType: CustomErrorType.Other,
                            textKey: TextKey.PolicyExceptionOverlapErrorMessage,
                        } satisfies CustomErrorParams,
                        path: [overlapIndex, PolicyExceptionsDatesFieldName.LastTimeUnitStartUtc],
                        fatal: true,
                    });
                });

                return timeIntervals;
            })
            .default([]),
    }),
});

export const policyExceptionSchema = datesPolicyExceptionSchema
    .merge(
        z.object({
            [FieldSectionName.Rates]: z.discriminatedUnion('exceptionType', [
                rateGroupExceptionTypeSchema,
                rateExceptionTypeSchema,
            ]),
        })
    )
    .merge(paymentPolicySchema)
    .merge(requirePaymentCardDuringOciExceptionTypeSchema);

export type PolicyExceptionFormValues = z.infer<typeof policyExceptionSchema>;

export const toPolicyExceptionAddParameters =
    ({ rateGroupId, serviceId }: WithServiceId & WithRateGroupId) =>
    (values: PolicyExceptionFormValues): PaymentAndCancellationPolicyOverrideAddParameters => ({
        rateGroupId,
        serviceId,
        timeUnitIntervals: values.dates.timeUnitIntervals,
        cancellationPolicyIds: array.isEmpty(values.cancellationFees ?? []) ? null : values.cancellationFees,
        paymentPolicyIds: values.settlementRules,
        rateIds: values.rates.exceptionType === 'rate' ? values.rates.rates : null,
        requirePaymentCardDuringOnlineCheckIn: values.requirePaymentCardDuringOnlineCheckIn ?? undefined,
    });

export const toPolicyExceptionUpdateParameters =
    ({ policyOverrideId, rateGroupId, serviceId }: WithServiceId & WithRateGroupId & Partial<WithPolicyOverrideId>) =>
    (values: PolicyExceptionFormValues): PaymentAndCancellationPolicyUpdateParameters => ({
        ...pipe(values, toPolicyExceptionAddParameters({ rateGroupId, serviceId })),
        policyOverrideId,
    });
